import { PaymentVerificationDto } from "@/services/admin-payment.service";
import { QrCode, Building2, Layers, Gift, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaymentMethodSummaryProps {
  payments: PaymentVerificationDto[];
  selectedMethod: string | null;
  onMethodClick: (method: string | null) => void;
  totalCount?: number;
}

interface MethodConfig {
  label: string;
  icon: LucideIcon;
  /**
   * Whether the card is drawn even at a count of zero. True for the methods this queue exists to
   * work through, so the layout does not jump around as the queue drains; false for one that only
   * ever reaches this screen by accident, whose empty card would be permanent furniture.
   */
  alwaysShow: boolean;
}

const METHOD_CONFIG: Record<string, MethodConfig> = {
  QRCode: {
    label: "QR Code",
    icon: QrCode,
    alwaysShow: true,
  },
  BankTransfer: {
    label: "Bank Transfer",
    icon: Building2,
    alwaysShow: true,
  },
  Trial: {
    // Settled from sign-up bonus credit, so it is born Completed and never enters this
    // AwaitingVerification queue — hence alwaysShow false. Configured all the same: without an
    // entry the card row silently omitted it while the "All Methods" count included it, so the
    // parts stopped adding up to the total the moment one ever did land here.
    label: "Trial (sign-up bonus)",
    icon: Gift,
    alwaysShow: false,
  },
  Balance: {
    // Settled from the customer's own wallet, so like Trial it is born Completed and never enters
    // this AwaitingVerification queue — hence alwaysShow false. Configured for the same reason
    // Trial is: the "All Methods" count includes it, so without an entry the cards stop adding up
    // to the total the moment one ever does land here.
    label: "Account Balance",
    icon: Wallet,
    alwaysShow: false,
  },
};

/** Fallback for a method the backend enum grows that this file has not been taught yet. */
const unknownMethodConfig = (method: string): MethodConfig => ({
  label: method,
  icon: Wallet,
  alwaysShow: false,
});

export function PaymentMethodSummary({
  payments,
  selectedMethod,
  onMethodClick,
  totalCount,
}: PaymentMethodSummaryProps) {
  // Calculate counts per method
  const methodCounts = payments.reduce(
    (acc, payment) => {
      const method = payment.paymentMethod;
      acc[method] = (acc[method] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // The per-method counts can only see the pages already loaded, so showing the server-side
  // total here would make "All" larger than QR + Bank Transfer. Show what is loaded, and say
  // how many exist in total when more are still to come.
  const loadedCount = payments.length;
  const hasMore = totalCount !== undefined && totalCount > loadedCount;

  // Cards are the always-on ones plus every method actually present among the loaded payments, so
  // the per-method counts always add up to the loaded total above. An unconfigured method used to
  // be counted in that total with no card of its own to account for it.
  const visibleMethods = [
    ...Object.keys(METHOD_CONFIG).filter(
      (method) => METHOD_CONFIG[method].alwaysShow || methodCounts[method] > 0,
    ),
    ...Object.keys(methodCounts).filter((method) => !METHOD_CONFIG[method]),
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {/* All Methods Card */}
      <button
        type="button"
        className={cn(
          "relative flex items-center justify-between rounded-xl border p-3.5 text-left transition-all duration-200 cursor-pointer shadow-xs hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          selectedMethod === null
            ? "border-primary bg-primary/[0.04] ring-2 ring-primary/20 shadow-sm"
            : "border-border/80 bg-card hover:bg-muted/40",
        )}
        aria-pressed={selectedMethod === null}
        onClick={() => onMethodClick(null)}
      >
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-0.5">All Methods</p>
          <p className="text-2xl font-bold tracking-tight text-foreground">
            {loadedCount}
            {hasMore && (
              <span className="ml-1 text-sm font-medium text-muted-foreground">
                / {totalCount}
              </span>
            )}
          </p>
        </div>
        <div
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
            selectedMethod === null
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground",
          )}
        >
          <Layers className="h-5 w-5" />
        </div>
      </button>

      {/* Method Cards */}
      {visibleMethods.map((method) => {
        const config = METHOD_CONFIG[method] ?? unknownMethodConfig(method);
        const count = methodCounts[method] || 0;
        const Icon = config.icon;
        const isActive = selectedMethod === method;

        return (
          <button
            key={method}
            type="button"
            className={cn(
              "relative flex items-center justify-between rounded-xl border p-3.5 text-left transition-all duration-200 cursor-pointer shadow-xs hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isActive
                ? "border-primary bg-primary/[0.04] ring-2 ring-primary/20 shadow-sm"
                : "border-border/80 bg-card hover:bg-muted/40",
            )}
            aria-pressed={isActive}
            onClick={() => onMethodClick(method)}
          >
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                <p className="text-xs font-medium text-muted-foreground">{config.label}</p>
              </div>
              <p className="text-2xl font-bold tracking-tight text-foreground">{count}</p>
            </div>
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
          </button>
        );
      })}
    </div>
  );
}
